/**
 * Parse an employee Excel (export or template) and preview creates / updates
 * before the user agrees to import.
 */

export function padCode(code) {
  const s = String(code || '').trim();
  if (/^\d+$/.test(s)) return s.padStart(4, '0');
  return s;
}

function headerIndex(headers, aliases) {
  const lower = headers.map((h) => String(h || '').trim().toLowerCase());
  for (const alias of aliases) {
    const i = lower.indexOf(alias);
    if (i >= 0) return i;
  }
  return -1;
}

function cellStr(row, index) {
  if (index < 0) return '';
  const v = row[index];
  if (v == null) return '';
  return String(v).trim();
}

export function parseName(fullName, firstName, lastName) {
  const first = String(firstName || '').trim();
  const last = String(lastName || '').trim();
  if (first && last) return { first_name: first, last_name: last, emp_name: `${last}, ${first}` };
  const raw = String(fullName || '').trim();
  if (!raw) return { first_name: '', last_name: '', emp_name: '' };
  if (raw.includes(',')) {
    const [ln, ...rest] = raw.split(',').map((s) => s.trim());
    const fn = rest.join(', ');
    return { first_name: fn, last_name: ln, emp_name: raw };
  }
  const bits = raw.split(/\s+/);
  if (bits.length >= 2) {
    const fn = bits[0];
    const ln = bits.slice(1).join(' ');
    return { first_name: fn, last_name: ln, emp_name: `${ln}, ${fn}` };
  }
  return { first_name: raw, last_name: '', emp_name: raw };
}

function normalizeStatus(raw) {
  const s = String(raw || '').trim().toLowerCase();
  if (s === 'terminated' || s === 'inactive') return 'terminated';
  if (s === 'leave' || s === 'on leave' || s === 'on_leave') return 'leave';
  return 'active';
}

/**
 * @param {string[]} headers 0-based header labels
 * @param {Array<Array>} dataRows 0-based cell values per row
 */
export function parseEmployeeImportRows(headers, dataRows) {
  const idx = {
    code: headerIndex(headers, ['code', 'employee code', 'emp_code', 'emp code']),
    name: headerIndex(headers, ['name', 'emp_name', 'employee name']),
    first: headerIndex(headers, ['first name', 'first_name']),
    last: headerIndex(headers, ['last name', 'last_name']),
    email: headerIndex(headers, ['email', 'empl_email', 'employee email']),
    status: headerIndex(headers, ['status']),
    dept: headerIndex(headers, ['dept', 'dept_code', 'department']),
    disc: headerIndex(headers, ['disc (=trade)', 'disc', 'discipline', 'discipline_code', 'trade']),
    role: headerIndex(headers, ['role', 'role_code']),
    note: headerIndex(headers, ['follow-up note', 'note (follow-up)', 'note', 'followup_note']),
  };
  if (idx.code < 0) return { rows: [], error: 'Could not find a Code / Employee Code column.' };

  const rows = [];
  for (const raw of dataRows) {
    const emp_code = padCode(cellStr(raw, idx.code));
    const parsed = parseName(cellStr(raw, idx.name), cellStr(raw, idx.first), cellStr(raw, idx.last));
    if (!emp_code || (!parsed.first_name && !parsed.last_name && !parsed.emp_name)) continue;
    rows.push({
      emp_code,
      ...parsed,
      empl_email: cellStr(raw, idx.email).toLowerCase(),
      status: normalizeStatus(cellStr(raw, idx.status)),
      dept_code: cellStr(raw, idx.dept).toUpperCase(),
      discipline_code: cellStr(raw, idx.disc).toUpperCase(),
      role_code: cellStr(raw, idx.role).toUpperCase(),
      followup_note: cellStr(raw, idx.note),
    });
  }
  return { rows, error: rows.length ? null : 'No valid employee rows found.' };
}

function existingName(row) {
  if (row.emp_name) return row.emp_name;
  const last = row.last_name || '';
  const first = row.first_name || '';
  return last && first ? `${last}, ${first}` : `${last}${first}`.trim();
}

export function buildEmployeeImportPreview(incoming, existing) {
  const byCode = new Map((existing || []).map((r) => [padCode(r.emp_code), r]));
  const created = [];
  const updated = [];
  const unchanged = [];

  for (const row of incoming) {
    const cur = byCode.get(row.emp_code);
    if (!cur) {
      created.push(row);
      continue;
    }
    const changes = [];
    const currentName = existingName(cur);
    const nextName = row.emp_name || existingName(row);
    if (String(currentName || '').trim() !== String(nextName || '').trim() && nextName) {
      changes.push({ field: 'Name', from: currentName || '(blank)', to: nextName });
    }
    const fields = [
      { key: 'empl_email', label: 'Email' },
      { key: 'status', label: 'Status' },
      { key: 'dept_code', label: 'Dept' },
      { key: 'discipline_code', label: 'Disc' },
      { key: 'role_code', label: 'Role' },
      { key: 'followup_note', label: 'Note' },
    ];
    for (const f of fields) {
      const from = String(cur[f.key] ?? '').trim();
      const to = String(row[f.key] ?? '').trim();
      if (to && from.toLowerCase() !== to.toLowerCase()) {
        changes.push({ field: f.label, from: from || '(blank)', to });
      }
    }
    if (changes.length) updated.push({ ...row, changes });
    else unchanged.push(row);
  }

  return { created, updated, unchanged };
}

export function incomingToBulkChanges(incoming, { existingCodes } = {}) {
  const known = existingCodes instanceof Set ? existingCodes : new Set(existingCodes || []);
  return incoming.map((r) => ({
    emp_code: r.emp_code,
    first_name: r.first_name,
    last_name: r.last_name,
    empl_email: r.empl_email || null,
    status: r.status,
    is_active: r.status === 'active',
    dept_code: r.dept_code || null,
    discipline_code: r.discipline_code || null,
    role_code: r.role_code || null,
    followup_note: r.followup_note || undefined,
    isNew: known.size ? !known.has(r.emp_code) : false,
  }));
}
